import { zodResolver } from "@hookform/resolvers/zod";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useInvitationActions } from "@/features/members/hooks/useInvitationActions";
import { useRequiredOrgId } from "@/hooks/useRequiredOrgId";

const invitationSchema = z.object({
  email: z.string().email(),
  role: z.enum(["admin", "member"]),
});

const formSchema = z.object({
  invitations: z.array(invitationSchema),
});

const MAX_INVITATIONS = 5;

export const CreateInvitationButton = () => {
  const organizationId = useRequiredOrgId();
  const [open, setOpen] = useState(false);
  const { createInvitations } = useInvitationActions();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      invitations: [{ email: "", role: "member" }],
    },
  });
  const invitations = form.watch("invitations");

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  function onSubmit(values: z.infer<typeof formSchema>) {
    createInvitations.mutate(
      { organizationId, invitations: values.invitations },
      {
        onSuccess: (response) => {
          if (!response) return;

          const { success, failure } = response;

          if (failure === 0 && success > 0) {
            toast.success("Invitations sent", {
              description: `Successfully sent ${success} invitation${success > 1 ? "s" : ""}`,
            });
            setOpen(false);
          } else if (success === 0 && failure > 0) {
            toast.error("Failed to send invitations", {
              description: `Failed to send ${failure} invitation${failure > 1 ? "s" : ""}`,
            });
          } else {
            toast.warning("Partial success", {
              description: `Successfully sent ${success} invitation${success > 1 ? "s" : ""}, but failed to send ${failure}`,
            });
          }
        },
        onError: (error: Error) => {
          toast.error("Error sending invitations", {
            description: error.message,
          });
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-11 gap-2 px-4 text-sm">
          <Plus className="size-3.5" />
          Add Users
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite Users</DialogTitle>
          <DialogDescription>
            Enter the email address and role for each user you wish to invite to
            the organization.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="pt-2 space-y-4"
          >
            <FormField
              control={form.control}
              name="invitations"
              render={({ field }) => (
                <div className="space-y-4">
                  {field.value.map((_, index) => (
                    <div key={index} className="flex gap-4 items-start">
                      <FormField
                        control={form.control}
                        name={`invitations.${index}.email`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input placeholder="Email" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`invitations.${index}.role`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel>Role</FormLabel>
                            <FormControl>
                              <select
                                {...field}
                                className="flex px-3 py-2 w-full h-10 text-sm rounded-md border border-input bg-background ring-offset-background"
                              >
                                <option value="member">Member</option>
                                <option value="admin">Admin</option>
                              </select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {index > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            const currentInvitations =
                              form.getValues("invitations");
                            form.setValue(
                              "invitations",
                              currentInvitations.filter((_, i) => i !== index),
                            );
                          }}
                          disabled={createInvitations.isPending}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            />

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const currentInvitations = form.getValues("invitations");
                form.setValue("invitations", [
                  ...currentInvitations,
                  { email: "", role: "member" },
                ]);
              }}
              disabled={
                createInvitations.isPending ||
                invitations.length >= MAX_INVITATIONS
              }
            >
              <Plus className="w-4 h-4" />
              Add
            </Button>

            <DialogFooter className="flex justify-end">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={createInvitations.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
